/**
 * MotionCard - Enhanced card with subtle hover animations
 * 
 * Features:
 * - Subtle lift effect (2px up)
 * - Slight opacity boost
 * - Shadow enhancement
 * - Respects prefers-reduced-motion
 * - Keyboard accessible (focus states match hover)
 */

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "../../lib/utils";
import { cardVariants, cardHover, shouldReduceMotion } from "../../config/motionConfig";

export interface MotionCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart' | 'onAnimationEnd' | 'onDragEnter' | 'onDragLeave' | 'onDragOver' | 'onDrop'> {
  /**
   * Disable hover animations
   * Useful for cards that don't need interaction feedback
   */
  disableAnimation?: boolean;
}

const MotionCard = React.forwardRef<HTMLDivElement, MotionCardProps>(
  ({ className, disableAnimation = false, ...props }, ref) => {
    const prefersReducedMotion = useReducedMotion();
    const shouldAnimate = !disableAnimation && !prefersReducedMotion;

    return (
      <motion.div
        ref={ref}
        className={cn(
          "rounded-lg border bg-card text-card-foreground shadow-sm",
          "transition-shadow duration-200", // Shadow transition via CSS for better performance
          shouldAnimate && "cursor-pointer",
          className
        )}
        variants={shouldAnimate ? cardVariants : undefined}
        initial="initial"
        whileHover={shouldAnimate ? "hover" : undefined}
        whileFocus={shouldAnimate ? "hover" : undefined}
        tabIndex={shouldAnimate ? 0 : undefined}
        style={
          shouldAnimate
            ? {
                // Use CSS for opacity to avoid layout recalculation
                willChange: "transform",
              }
            : undefined
        }
        onHoverStart={() => {
          if (shouldAnimate && typeof window !== "undefined") {
            // Subtle opacity boost via inline style for performance
            const element = ref as React.RefObject<HTMLDivElement>;
            if (element?.current) {
              element.current.style.opacity = String(1 + cardHover.opacityBoost);
            }
          }
        }}
        onHoverEnd={() => {
          if (shouldAnimate && typeof window !== "undefined") {
            const element = ref as React.RefObject<HTMLDivElement>;
            if (element?.current) {
              element.current.style.opacity = "1";
            }
          }
        }}
        {...props}
      />
    );
  }
);

MotionCard.displayName = "MotionCard";

// Re-export existing card components with motion support
const MotionCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
));
MotionCardHeader.displayName = "MotionCardHeader";

const MotionCardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
MotionCardTitle.displayName = "MotionCardTitle";

const MotionCardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
MotionCardDescription.displayName = "MotionCardDescription";

const MotionCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
MotionCardContent.displayName = "MotionCardContent";

const MotionCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
));
MotionCardFooter.displayName = "MotionCardFooter";

export {
  MotionCard,
  MotionCardHeader,
  MotionCardFooter,
  MotionCardTitle,
  MotionCardDescription,
  MotionCardContent,
};
